<template>
  <div
    class="v-tooltip__wrapper"
    :class="{
      [extraClass]: true,
      [size]: true,
      ['ellipsis']: !noEllipsis,
      [theme]: true,
      [align]: true,
      ['preLine']: preLine,
      ['noPadding']: noPadding
    }"
    :style="{ maxWidth: maxWidth, minWidth: minWidth }"
  >
    <span :tooltip="text" :flow="position" :mwidth="maxWidth">
      <slot></slot>
    </span>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

export default Vue.component("VTooltip", {
  props: {
    text: VueTypes.string.isRequired,
    size: VueTypes.oneOf(["small", "big", "fit"]).def("small"),
    position: VueTypes.oneOf(["up", "down", "left", "right"]).def("right"),
    extraClass: VueTypes.string.def(""),
    noEllipsis: VueTypes.bool.def(false),
    maxWidth: VueTypes.string,
    minWidth: VueTypes.string,
    theme: VueTypes.oneOf(["light", "dark"]).def("light"),
    align: VueTypes.oneOf(["left", "right", "center"]).def("center"),
    noPadding: VueTypes.bool.def(false),
    preLine: VueTypes.bool.def(false)
  }
});
</script>

<style lang="scss">
$ns: "v-tooltip";
$font-size: 14px;

.#{$ns} {
  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    cursor: default;

    &.center {
      [tooltip]::after {
        text-align: center;
      }
    }

    &.left {
      [tooltip]::after {
        text-align: left;
      }
    }

    &.right {
      [tooltip]::after {
        text-align: left;
      }
    }

    &.no-pad {
      [tooltip][flow^="up"]::after {
        padding: 0;
      }
    }

    span {
      display: inline-block;
    }

    [tooltip] {
      position: relative;

      /* Applies to all tooltips */
      &::before,
      &::after {
        text-transform: none;
        font-size: $font-size;
        line-height: 1;
        user-select: none;
        pointer-events: none;
        position: absolute;
        display: none;
        opacity: 0;
        color: $secondary-text-color;
      }

      &::before {
        content: "";
        z-index: 9999;
        background-size: 7px 7px;
        height: 7px;
        width: 7px;
      }

      &::after {
        content: attr(tooltip);
        text-align: center;
        padding: 20px 15px;
        background: $white;
        z-index: 9998;
        color: #979797;
        font-size: $font-size;
        font-family: Lato-Regular, sans-serif;
        line-height: 1.2;
        min-height: 22px;
        border-radius: 3px;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.15);
      }

      /* Make the tooltips respond to hover */
      &:hover {
        &::before,
        &::after {
          display: flex;
          align-items: center;
        }
      }

      /* FLOW: UP */
      &:not([flow]),
      &[flow^="up"] {
        align-items: flex-start;

        &::before {
          border-bottom-width: 0;
          border-top-color: $white;
          bottom: calc(100% + 6px);
          background: url("../assets/images/tooltip-triangle-bottom.svg")
            no-repeat;
        }

        &::after {
          color: #979797;
          bottom: calc(100% + 10px);
        }

        &::before,
        &::after {
          left: 50%;
          transform: translate(-50%, -10px);
        }
      }

      /* FLOW: DOWN */
      &[flow^="down"] {
        &::before {
          top: 100%;
          border-top-width: 0;
          border-bottom-color: $white;
        }

        &::after {
          top: calc(100% + 10px);
        }

        &::before,
        &::after {
          left: 50%;
          transform: translate(-50%, 0.5em);
        }
      }

      /* FLOW: LEFT */
      &[flow^="left"] {
        &::before {
          top: 50%;
          border-right-width: 0;
          border-left-color: $white;
          left: calc(0em - 10px);
        }

        &::after {
          top: 50%;
          right: calc(100% + 10px);
          transform: translate(-0.5em, -50%);
        }
      }

      /* FLOW: RIGHT */
      &[flow^="right"] {
        &::before {
          top: 50%;
          border-left-width: 0;
          border-right-color: $white;
          right: calc(0em - 10px);
          background: url("../assets/images/tooltip-triangle-left.svg")
            no-repeat;
        }

        &::after {
          display: flex;
          top: 50%;
          left: calc(100% + 10px);
          transform: translate(0.5em, -50%);
        }
      }
    }

    /*FOR DIFFERENT SIZE PROP*/
    &.small {
      [tooltip]::after {
        min-width: 80px;
      }
    }

    &.big {
      [tooltip]::after {
        min-width: 200px;
      }
    }

    &.fit {
      [tooltip]::after {
        min-width: max-content;
      }
    }
    /*----------------------*/

    &.ellipsis {
      [tooltip]::after {
        @include textCropEllipsis();
      }
    }

    &.dark {
      [tooltip] {
        &::after {
          background-color: #444;
          color: white;
          white-space: pre-line;
          line-height: 1.2;
        }
        /* FLOW: UP */
        &:not([flow]),
        &[flow^="up"] {
          &::before {
            border-top-color: #444;
            background: none;
          }
        }
        /* FLOW: DOWN */
        &[flow^="down"] {
          &::before {
            border-bottom-color: #444;
          }
        }
        /* FLOW: LEFT */
        &[flow^="left"] {
          &::before {
            border-left-color: #444;
          }
        }
        /* FLOW: RIGHT */
        &[flow^="right"] {
          &::before {
            border-right-color: #444;
          }
        }
      }
    }

    &.noPadding {
      [tooltip] {
        &::after {
          padding: 0 10px;
        }
      }
    }

    &.preLine {
      [tooltip] {
        &::after {
          white-space: pre-line;
        }
      }
    }

    /* don't show empty tooltips */
    [tooltip=""]::before,
    [tooltip=""]::after {
      display: none !important;
    }

    /* KEYFRAMES */
    @keyframes tooltips-vert {
      to {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }

    @keyframes tooltips-horz {
      to {
        opacity: 1;
        transform: translate(0, -50%);
      }
    }

    /* FX All The Things */
    [tooltip] {
      &:not([flow]),
      &[flow^="up"],
      &[flow^="down"] {
        &:hover {
          &::before,
          &::after {
            animation: tooltips-vert 100ms ease-out forwards;
          }
        }
      }

      &[flow^="left"],
      &[flow^="right"] {
        &:hover {
          &::before,
          &::after {
            animation: tooltips-horz 100ms ease-out forwards;
          }
        }
      }
    }
  }
}
</style>
